// @fileoverview : 기준정보 > 품목분류
import { mutatePurchaseOrderCategories } from "@/api/purchaseOrderCategories/usePurchaseOrderCategoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { PurchaseOrderCategoryCreateForm } from "@/features/purchaseOrderCategory/form/CreateForm";
import { PurchaseOrderCategoryDetailForm } from "@/features/purchaseOrderCategory/form/DetailForm";
import { PurchaseOrderCategoryUpdateForm } from "@/features/purchaseOrderCategory/form/UpdateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";

import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  PurchaseOrderCategoriesGet200Response,
  PurchaseOrderCategoriesGet200ResponseRowsInner,
  ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const PurchaseOrderCategoryPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { t } = useTranslation();

  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdDeleteRequest) =>
      mutatePurchaseOrderCategories
        .delete(params)
        .mutationFn(
          params as ScmApiPurchaseOrderCategoriesPurchaseOrderCategoryIdDeleteRequest | any,
        ),
    {
      onSuccess: (error) => {
        refetch();
        customAlert(
          `${t("message.deleteSuccess", { item: `${t("purchaseOrderCategory.category")}` })}`,
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("purchaseOrderCategory.name") }),
          `${t("common.deleteFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
          "red",
        );
      },
    },
  );

  const columns: readonly Column<PurchaseOrderCategoriesGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "name",
      name: t("purchaseOrderCategory.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PurchaseOrderCategoryDetailForm
                  PurchaseOrderCategoryName={formatterProps.row.name}
                />,
                null,
                "",
              )
            }
          >
            {formatterProps.row.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updatePurchaseOrderCategoryActionView = () => {
          openModal(
            <PurchaseOrderCategoryUpdateForm formatterProps={formatterProps?.row} />,
            null,
            `${t("common.edit", { item: `${t("purchaseOrderCategory.category")}` })}`,
            true,
          );
        };

        const deletePurchaseOrderCategoryAction = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() =>
                deleteMutate({ purchaseOrderCategoryId: formatterProps?.row.id as number })
              }
              noCallback={() => {}}
              commonCallback={() => closeModal(null)}
            />,
            null,
            `${t("common.delete", { item: `${t("purchaseOrderCategory.category")}` })}`,
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon
              variant="subtle"
              onClick={updatePurchaseOrderCategoryActionView}
              color="yellow"
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deletePurchaseOrderCategoryAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: `${t("purchaseOrderCategory.category")}` })}`,
        `${t("common.deleteFail", { item: `${t("purchaseOrderCategory.category")}` })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderCategoryId: row });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: `${t("purchaseOrderCategory.category")}` })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <PurchaseOrderCategoryCreateForm />,
              null,
              `${t("common.additional", { item: `${t("purchaseOrderCategory.category")}` })}`,
              true,
            )
          }
          createDataTitle={t("purchaseOrderCategory.category")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", { item: `${t("purchaseOrderCategory.category")}` })}
          </Text>
          <Text fz="xs">
            {t("common.subTitle", { item: `${t("purchaseOrderCategory.category")}` })}
          </Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<
            PurchaseOrderCategoriesGet200Response,
            PurchaseOrderCategoriesGet200ResponseRowsInner
          >
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrderCategoriesGet200Response> />
      </Flex>
    </Flex>
  );
};
